import { Box, Card, Flex, Text } from '@mantine/core';
import { GetEnergyConsumptionResponse } from 'api/actions/get-energy-consumption/get-energy-consumption-response';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, TooltipItem } from 'chart.js';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { xScaleConfig, yGridConfig, barDatasetBase } from 'pages/tuya-device/components/chartHelpers';
import useLang from 'lang';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

type SpotPrice = NonNullable<GetEnergyConsumptionResponse['spotPrices']>[number];

interface SpotPricesChartCardProps {
  /** Raw spot price entries for the day */
  spotPrices: SpotPrice[];
  /** The date string from the API response */
  date: string;
}

/**
 * Displays a bar chart of electricity spot prices (€/MWh) across the day.
 *
 * @prop {SpotPrice[]} spotPrices - Array of spot price entries with delivery windows.
 * @prop {string} date - ISO date string for the chart title.
 */
export default function SpotPricesChartCard({ spotPrices, date }: SpotPricesChartCardProps) {
  const { _t } = useLang();
  const chartData = useMemo(
    () => ({
      labels: spotPrices.map((sp) => {
        const d = new Date(sp.deliveryStart!);
        return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
      }),
      datasets: [
        {
          ...barDatasetBase,
          label: _t('Spot price (€/MWh)'),
          data: spotPrices.map((sp) => sp.price || 0),
          backgroundColor: '#10B981',
          hoverBackgroundColor: '#059669',
        },
      ],
    }),
    [_t, spotPrices]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ...xScaleConfig },
        y: {
          ticks: {
            color: theme.colors?.gray?.[7],
            callback: (v: number | string) => `${Number(v).toFixed(2)} €/MWh`,
          },
          grid: yGridConfig,
          beginAtZero: true,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: { label: (ctx: TooltipItem<'bar'>) => `${_t('Spot price')}: ${ctx.parsed.y.toFixed(2)} €/MWh` },
        },
      },
    }),
    [_t]
  );

  const prices = spotPrices.map((sp) => sp.price || 0);
  const avgPrice = prices.length ? (prices.reduce((s, p) => s + p, 0) / prices.length).toFixed(2) : '0';
  const minPrice = prices.length ? Math.min(...prices).toFixed(2) : '0';
  const maxPrice = prices.length ? Math.max(...prices).toFixed(2) : '0';

  return (
    <Card p={20} radius={10} shadow="sm" mt={20}>
      <H2SemiBold mb={20}>
        {_t('Electricity spot prices')} - {new Date(date).toLocaleDateString('sk-SK')}
      </H2SemiBold>
      <Box style={{ height: 400 }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>

      <Box mt={20} p={15} style={{ backgroundColor: '#F0FDF4', borderRadius: 8, border: '1px solid #D1FAE5' }}>
        <Flex justify="space-between" align="center">
          <Text size="lg" fw={600} c="gray.7">
            {_t('Average spot price')}:
          </Text>
          <Text size="xl" fw={700} c="#10B981">
            {avgPrice} €/MWh
          </Text>
        </Flex>
        <Flex justify="space-between" align="center" mt={10}>
          <Text size="sm" c="gray.6">
            Min: {minPrice} €/MWh
          </Text>
          <Text size="sm" c="gray.6">
            Max: {maxPrice} €/MWh
          </Text>
        </Flex>
      </Box>
    </Card>
  );
}
